import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Award,
  Baby,
  Boxes,
  Gift,
  PersonStanding,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";

export const babyKidsPrompts: CategoryPrompt[] = [
  {
    icon: <ShieldCheck />,
    title: "Safety & Ingredient Education Listicle",
    description:
      "Build a listicle addressing parent safety concerns, showcasing my safe alternatives with prominent certifications and CTAs throughout.",
    prompt: `Build a safety-focused listicle that addresses parent anxieties and positions your products as the certified, worry-free alternatives for babies and kids.

**TOPIC:** e.g., "7 Hidden Dangers in Baby Products (And Safe Alternatives)" OR "5 Toxic Ingredients to Avoid in Kids' Items" OR "What Pediatricians Wish You Knew About [Product Type]"

**SAFE PRODUCTS FEATURED:** e.g., "Our non-toxic sippy cups" OR "Certified organic baby clothing line"

**Parent Reader Type:** Choose one:
- First-time parents (hyper-cautious, overwhelmed)
- Health-conscious families (researching everything)
- Burned by past products (seeking trustworthy brands)

**Tone & Style:**
- Protective but not fear-mongering
- Empathetic to parent anxiety
- Science-backed and credible
- Empowering through education

**CTA Guidelines:** Each CTA should:
- Emphasize safety certifications ("Certified Safe")
- Use parent-focused language ("Protect Your Baby")
- Reference peace of mind ("Worry-Free Choice")
- Include trust badges (organic, non-toxic, tested)

**Conversion Tactics Use:**
- Certification badge display (GOTS, OEKO-TEX, CPSIA, etc.)
- Lab testing results and third-party validation
- Pediatrician endorsements or quotes
- Before/after safety comparisons
- Ingredient transparency (what's NOT in products)
- Parent testimonials focused on safety relief

**Key Elements to Include:**
- **Hook:** Relatable parent fear ("When I learned what was in my baby's...")
- **Safety Promise:** "We'll show you what to avoid and what's truly safe"
- **List Items (5-8 dangers):**
  - **Danger Headline:** "Danger #X: [Specific chemical/issue]"
  - **Why It's Harmful:** Science-backed explanation (cite studies)
  - **Where It Hides:** Common products containing this
  - **Health Risks:** Specific concerns for babies/kids
  - **Safe Alternative:** Your product as the solution
  - **Certifications Displayed:** Badges showing safety compliance
  - **What We Use Instead:** Specific safe ingredient/material
  - **Parent Testimonial:** "I switched and feel so much better knowing..."
- **Certification Callout Boxes:** 2-3 detailed explanations of key certifications
- **Ingredient Comparison:** Toxic brand vs. your safe formula (side-by-side)
- **Lab Test Results:** Third-party safety testing data
- **Pediatrician Quote:** Expert validation of safety concerns
- **Safety Checklist:** "What to Look For" downloadable guide
- **CTAs:** After danger #2, after danger #5, conclusion, with each product (6-8 total)
- **FAQ:** 4-5 questions (Are these really safer? Cost difference? Where certified? Age ranges?)

**Goal:** Convert anxious parents into loyal customers by educating them on real safety risks while positioning your certified, transparent products as the obvious safe choice that lets them parent with confidence, not fear.`,
  },
  {
    icon: <Award />,
    title: "Parent Testimonial Advertorial",
    description:
      "Build an advertorial featuring authentic parent testimonials, before/after photos, and expert quotes, with product CTAs and retargeting for non-purchasers.",
    prompt: `Build a parent testimonial advertorial that showcases real family stories and transformations to drive trust and conversions for baby/kids products.

**STORY FOCUS:** e.g., "How This Mom Finally Got Her Baby to Sleep Through the Night" OR "The Diaper Rash Solution 500+ Parents Swear By" OR "How We Stopped Mealtime Battles for Good"

**PRODUCT FEATURED:** e.g., "Our sleep sack system" OR "Organic diaper cream" OR "Toddler feeding set"

**Parent Story Type:** Choose one:
- Problem-to-solution transformation (struggled → found answer)
- Skeptic-to-believer (doubted → now advocates)
- Last-resort success (tried everything → this worked)

**Tone & Style:**
- Authentic parent voice (real struggles, honest emotions)
- Relatable and vulnerable
- Relief and gratitude focused
- Community-building ("you're not alone")

**CTA Guidelines:** Each CTA should:
- Reference parent success ("Try What Worked for Sarah")
- Use emotional language ("Finally Get Relief")
- Include social proof nearby ("Join 12,000+ Parents")
- Emphasize risk-free trial ("30-Day Money-Back")

**Conversion Tactics Use:**
- Multiple parent stories (not just one)
- Before/after photos of baby/child
- Timeline of improvement ("Week 1 vs. Week 4")
- Pediatrician validation quotes
- Failed alternatives mentioned
- Other parent comments ("This worked for us too!")
- Limited-time testimonial discount code

**Key Elements to Include:**
- **Headline:** Parent's result + timeframe ("How I Solved [Problem] in 2 Weeks")
- **Hero Image:** Parent + child photo (authentic, not stock)
- **Opening Struggle:** Detailed problem description (2-3 paragraphs, emotional)
- **Breaking Point:** "I was desperate/exhausted/at my wit's end when..."
- **Discovery Story:** How they found your product (feels organic)
- **Initial Skepticism:** "I wasn't sure it would work because..."
- **First Experience:** Week-by-week or day-by-day progress
- **Before/After Details:**
  - Before: Specific struggles (sleep hours, diaper changes, meltdowns)
  - After: Measurable improvements (numbers, frequency, behavior)
  - Photos: Real family photos showing change
- **Product Callout Boxes:** 2-3 highlighted features that solved their problem
- **Featured Parent Quote Pulls:** Emotional statements as visual breaks
- **Other Parent Testimonials:** 4-6 supporting stories (shorter versions)
- **Expert Validation:** Pediatrician or child development specialist quote
- **Why It Works:** Science or design explanation
- **Common Concerns Addressed:** From featured parent's perspective
- **CTAs:** After struggle, after discovery, after results, conclusion (5-7 total)
- **FAQ from Parent POV:** "What I wish I knew before starting"
- **Community Proof:** "Sarah isn't alone - read more stories below"

**Goal:** Convert skeptical parents into confident buyers by showcasing authentic transformation stories that make product benefits tangible, relatable, and emotionally compelling - proving real families got real results.`,
  },
  {
    icon: <Baby />,
    title: "Life Stage Product Finder Quiz",
    description:
      "Build a quiz asking about life stage, needs, and budget, then generate personalized recommendations, gated behind email capture.",
    prompt: `Build a life stage quiz that matches parents to age-appropriate baby/kids products based on developmental needs and priorities, gated with email capture.

**QUIZ FOCUS:** e.g., "Find Your Baby's Essential Products" OR "What Does Your Toddler Actually Need?" OR "Your Child's Stage-Perfect Kit"

**PRODUCT RANGE:** e.g., Newborn to 5 years across categories (feeding, sleep, play, safety) with 15-25 products to match from

**Parent Quiz Taker:** Choose one:
- Expecting parents (planning ahead, overwhelmed)
- New parents (need guidance now)
- Experienced parents (new stage, new needs)

**Tone & Style:**
- Supportive parenting guide
- Non-judgmental and reassuring
- Age-appropriate language
- Practical over perfectionist

**Quiz Question Guidelines:** Each question should:
- Use developmental milestones as markers
- Offer visual answer options (icons or baby/child photos)
- Address real parent priorities
- Avoid overwhelming medical jargon
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #3 or #4 (mid-commitment)
- Frame as "Get your personalized essentials list"
- Offer incentive: "Unlock matches + 20% new parent discount"
- Single field: email only (+ optional due date/child age)
- Trust line: "Join 25,000+ parents who found their perfect products"

**Results Page Elements:**
- **Personalized Headline:** "Perfect for your [age] [stage descriptor], here's what you need"
- **Top 3 Must-Haves (Hero):** Essential products for their stage with "Why now" explanations
- **Complete Stage Kit:** 5-8 products bundled as recommended starter set
- **Nice-to-Have Add-Ons:** 2-3 optional comfort/convenience items
- **Next Stage Preview:** "Coming soon for your little one" (builds return visits)
- **Product Cards Include:** Image, age range, key benefit, parent rating, stage-specific feature
- **Match Reasoning:** "Your baby is [age], so they need [feature] because..."
- **Developmental Callouts:** Why each product fits this milestone
- **Bundle Options:** Complete kit vs. individual purchases with savings
- **CTAs on Each Product:** "Shop Essential" or "Add to Registry"
- **Stage Timeline:** Visual showing current stage + what's coming
- **Retake Option:** "Stage changed? Update your results"

**Life Stage Matching Examples:**
- **Newborn (0-3mo):** Swaddles, bassinet, feeding essentials, soothing items
- **Sitting Baby (4-6mo):** High chair, teething toys, activity mat, sippy cup intro
- **Crawler (7-9mo):** Safety gates, soft toys, finger foods gear, knee pads
- **New Walker (10-14mo):** Walking shoes, push toys, cabinet locks, spill-proof dishes
- **Toddler (15-24mo):** Potty training, independence tools, learning toys, big kid utensils
- **Preschooler (3-5yr):** Creative play, outdoor gear, social development, routine helpers

**Goal:** Convert overwhelmed parents into confident buyers by providing expert-curated, age-appropriate product recommendations that eliminate guesswork and ensure they buy exactly what their child needs right now - nothing more, nothing less.`,
  },
  {
    icon: <PersonStanding />,
    title: "Subscription Landing Page",
    description:
      "Build a subscription page emphasizing convenience and savings, with savings calculator and flexibility to pause or cancel anytime.",
    prompt: `Build a subscription landing page that converts one-time buyers into recurring customers by emphasizing effortless convenience, savings, and total flexibility for busy parents.

**SUBSCRIPTION OFFER:** e.g., "Diaper Delivery Club" OR "Monthly Baby Essentials Box" OR "Toddler Snack Subscription"

**VALUE PROPOSITION:** e.g., "Never run out + save 25%" OR "Curated age-appropriate products monthly"

**Subscription Model:** Choose one:
- Replenishment (diapers, wipes, formula - same products)
- Curated boxes (age-appropriate surprises each month)
- Build-your-own (parent selects products + frequency)

**Tone & Style:**
- Time-saving and stress-relieving
- Parent empathy ("one less thing to remember")
- Savings-focused without cheapness
- Flexibility and control emphasized

**CTA Guidelines:** Each CTA should:
- Emphasize convenience ("Never Run Out Again")
- Reference savings ("Start Saving 25% Today")
- Use parent language ("Make Life Easier")
- Include flexibility reminder ("Pause or Cancel Anytime")

**Conversion Tactics Use:**
- Time-back calculation ("Save 3 hours/month on shopping trips")
- Annual savings calculator with visual impact
- First box special offer ("50% off first delivery")
- Comparison: store runs vs. doorstep delivery
- Testimonials from time-strapped parents
- Guarantee: "Skip, pause, or cancel with one click"

**Key Elements to Include:**
- **Hero Section:** Product image, headline with savings %, "Start Subscription" CTA
- **Parent Pain Point:** "Stop the 11pm diaper run" or "No more forgetting essentials"
- **Value Proposition:** 3-4 benefits (save money, save time, never run out, age-appropriate)
- **Pricing Comparison:** Side-by-side subscription vs. retail price
- **Savings Calculator:** Monthly savings, annual savings, time saved with visual impact
- **How It Works:** 3-step process (Choose products → Set schedule → Delivered automatically)
- **Customization Options:** Frequency selector, quantity adjuster, product variants, age-based auto-updates
- **Flexibility Features:** Skip, pause, cancel badges with "Change anytime" copy
- **First Box Incentive:** "Get 50% off your first delivery + free gift"
- **Subscriber Perks:** 25% off, free shipping, early access, birthday surprises
- **Social Proof:** "32,000+ parents subscribe" + parent testimonials
- **Age-Progression Feature:** "We auto-adjust as your baby grows"
- **CTAs:** Hero, after pricing, after how-it-works, after testimonials, footer (5-6 total)
- **FAQ:** 6-8 questions (Cancel policy? Change products? Skip months? Shipping?)
- **Risk Reversal:** "Try first box risk-free - cancel before 2nd shipment, no charge"

**Goal:** Convert time-strapped, budget-conscious parents into loyal subscribers by emphasizing massive convenience, guaranteed savings, and risk-free flexibility that makes subscription feel like the obvious smart choice over repeated store trips.`,
  },
  {
    icon: <RotateCcw />,
    title: "Customer Photo Gallery",
    description:
      "Build a gallery featuring customer-submitted photos and reviews, with star ratings for authentic social proof.",
    prompt: `Build a customer photo gallery showcasing real families using your products to provide authentic social proof that drives trust and conversions.

**GALLERY FOCUS:** e.g., "Real Families, Real Moments" OR "See Our Products in Action" OR "Parents Love, Babies Love"

**PHOTO CATEGORIES:** e.g., By product, by age, by use case, by rating (4-5 stars only)

**Gallery Visitor Type:** Choose one:
- Research-phase shoppers (need validation before buying)
- Skeptical parents (want proof it works for real families)
- Gift buyers (want to see product in real life)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of real parenting moments
- Community-focused ("you're part of this")
- No stock photos - only real customers

**CTA Guidelines:** Each CTA should:
- Use social proof language ("Join 15,000+ Happy Parents")
- Reference community ("See Why Parents Love This")
- Include trust element ("Rated 4.8/5 by Real Families")
- Link directly to featured products

**Conversion Tactics Use:**
- Filter by product to see specific item proof
- Star ratings prominently displayed
- Purchase verification badges
- "Most helpful" review sorting
- Photo count per product ("2,400+ customer photos")
- Incentive for photo submission (discount for next purchase)

**Key Elements to Include:**
- **Hero Section:** Headline, photo grid preview, filter options, "Submit Your Photo" CTA
- **Filter Options:** By product category, age, rating, verified purchase, sort order
- **Photo Grid Layout:** Large grid with hover-to-expand, product tags, verified badges
- **Featured Photo Stories (3-5):** Large hero image, full review, multiple photos, product links
- **Star Rating Summary:** Overall rating, breakdown bar graph, recommendation percentage
- **Social Proof Metrics:** "32,000+ photos submitted", "15,000+ verified purchases"
- **Product-Specific Galleries:** Dedicated section per top product with photos and ratings
- **Photo Submission Section:** "Share Your Photo & Get 15% Off Next Order"
- **Authenticity Badges:** Verified purchase, real names, submission dates
- **CTAs:** Top of page, with featured stories, product galleries, submission section (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Privacy? When featured? Incentive details?)

**Goal:** Convert hesitant shoppers into confident buyers by showcasing authentic, unfiltered customer photos and reviews that prove real families love and trust your products - making the social proof impossible to ignore.`,
  },
  {
    icon: <Boxes />,
    title: "Expert Endorsement Page",
    description:
      "Create a page featuring expert endorsements with credentials, ingredient breakdowns, and safety certifications addressing parent concerns.",
    prompt: `Build an expert endorsement page that leverages professional credibility and scientific validation to address parent safety concerns and build unshakeable trust.

**EXPERT FOCUS:** e.g., "Pediatrician-Approved Products" OR "What Child Development Experts Recommend" OR "Trusted by Medical Professionals"

**EXPERT TYPES FEATURED:** e.g., Pediatricians, child psychologists, nutritionists, safety specialists, lactation consultants

**Parent Audience:** Choose one:
- Safety-anxious first-time parents (need expert validation)
- Research-driven parents (want scientific backing)
- Skeptical shoppers (burned by false marketing claims)

**Tone & Style:**
- Credible and authoritative
- Science-backed without being condescending
- Reassuring and confidence-building
- Transparent about credentials

**CTA Guidelines:** Each CTA should:
- Reference expert approval ("Pediatrician-Recommended")
- Use credential language ("MD-Approved Choice")
- Include certification badges nearby
- Emphasize safety/quality ("Expert-Verified Safe")

**Conversion Tactics Use:**
- Multiple expert types (not just one opinion)
- Specific credential displays (MD, PhD, board certifications)
- Quote pulls from expert interviews
- Video testimonials from professionals
- Clinical study references
- Comparison: our standards vs. industry minimums
- Third-party testing results

**Key Elements to Include:**
- **Hero Section:** "Trusted by Pediatricians & Parents Alike" + expert photo collage + primary CTA
- **Expert Advisory Board:** 4-6 featured experts with photos, credentials, affiliations, bios
- **Featured Expert Endorsements (3-5):** Headshot, full credentials, "Why I Recommend" statement, video if available
- **Product-Specific Expert Quotes:** Each major product gets expert quote with credentials
- **Ingredient Breakdown Section:** "What's Inside (And What's NOT)" with scientific sources
- **Safety Certification Display:** Large badge visuals with explanations of each certification
- **Clinical Study References:** 3-5 relevant studies with brief summaries
- **Expert Q&A Section:** 5-7 common safety concerns answered by experts
- **Comparison Chart:** Our standards vs. industry standards
- **Parent Testimonials Mentioning Experts:** "My pediatrician recommended this brand"
- **CTAs:** Hero, after expert board, with product sections, after certifications (5-6 total)
- **FAQ:** 5-6 questions (Are endorsements paid? How are experts chosen? All products approved?)

**Goal:** Convert skeptical, safety-conscious parents into confident buyers by providing overwhelming expert validation, scientific backing, and certification proof that makes choosing your brand feel like the only responsible decision for their child's health and safety.`,
  },
  {
    icon: <Gift />,
    title: "Clearance Sale Campaign",
    description:
      "Build a clearance page with variant selectors, inventory indicators, and bulk discounts, using urgency messaging and cross-sell recommendations to maximize AOV.",
    prompt: `Build a clearance sale page that drives urgency and maximizes order value through strategic bundling, real-time inventory, and smart cross-sells.

**SALE FOCUS:** e.g., "End-of-Season Baby Gear Clearance" OR "Last Chance Kids' Items"

**DISCOUNT DEPTH:** e.g., "30-70% off" OR "Buy 2 save 40%, Buy 3 save 50%"

**Shopper Type:** Choose one:
- Deal hunters (motivated by discounts)
- Budget-conscious parents (need quality for less)
- Stock-up shoppers (buying ahead)

**Tone & Style:**
- Urgent but not pushy
- Value-focused and exciting
- Clear about limited availability
- Honest about final sale terms

**CTA Guidelines:** Each CTA should:
- Emphasize scarcity ("Only X Left")
- Reference discount depth ("Save 60%")
- Use urgency language ("Final Hours")
- Update with inventory changes

**Conversion Tactics Use:**
- Real-time inventory counters
- Tiered bulk discounts (progressive savings)
- Countdown timer to sale end
- Smart cross-sells (complete the set)
- Free shipping threshold progress
- "Almost gone" alerts

**Key Elements to Include:**
- **Hero Section:** Sale headline with max discount %, countdown timer, primary CTA
- **Sale Countdown Timer:** Days:hours:minutes (large, prominent, sticky)
- **Bulk Discount Tier Banner:** "Buy 2: 40% | Buy 3: 50% | Buy 4+: 60%" with progress bar
- **Filter Options:** By discount %, category, age, size, in-stock toggle
- **Product Grid:** Discount badge, strikethrough price, sale price, inventory indicator, variant selector, quick-add
- **Inventory Indicators:** Color-coded (green: in stock, yellow: low, red: almost gone, gray: sold out)
- **Variant Selectors:** Size/color dropdowns showing stock per option
- **Cross-Sell Sections:** "Complete the Set" and "Stock Up Bundle" suggestions
- **Bulk Discount Progress (Sidebar):** Current tier achieved + items needed for next tier
- **Free Shipping Bar:** Progress to threshold with suggested items
- **Social Proof:** Live purchase ticker, shoppers viewing now
- **Final Sale Terms:** Clear "All sales final" disclosure
- **CTAs:** Hero, on each product, bulk prompts, sticky checkout (always visible)
- **FAQ:** 4-5 questions (Returns? Exchanges? Why clearance? Restock?)

**Goal:** Maximize AOV from clearance inventory through urgency, tiered bulk discounts, and strategic cross-selling while maintaining transparency about final sale terms.`,
  },
];
